import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../config";
import {
  TrendingUp, Repeat2, ArrowLeftRight,
  Search, BookOpen, BookMarked, Zap, Trophy,
  Target, Clock,
} from "lucide-react";
import "../styles/selectGameMode.css";
import PageHeader from "../components/PageHeader";
import apiFetch from "../apiFetch";

const CATEGORIES = {
  vocabulary: {
    title: "Vocabulary",
    subtitle: "Words are your weapons — sharpen them with matching, synonyms and antonyms",
    games: [
      {
        id: "vocab",
        title: "Vocabulary Matching",
        description: "Match words to their definitions — climb the vocab tiers and unlock owls",
        icon: <TrendingUp className="w-8 h-8" />,
        difficulty: "Scaling",
        estimatedTime: "1–5 min",
        color: "from-green-500 to-green-600",
        status: "Test your vocab limits",
        rewards: "Reach vocab tiers to unlock owls",
        link: "/vocabMatchingGame",
      },
      {
        id: "synonyms",
        title: "Synonyms",
        description: "Find the perfect match — train your brain to connect words that mean the same thing",
        icon: <Repeat2 className="w-8 h-8" />,
        difficulty: "Scaling",
        estimatedTime: "1–5 min",
        color: "from-teal-500 to-teal-600",
        status: "Test your word knowledge",
        rewards: "Earn coins and ELO",
        link: "/synonymsGame",
      },
      {
        id: "antonyms",
        title: "Antonyms",
        description: "Think in opposites — master contrasts and make your vocabulary twice as powerful",
        icon: <ArrowLeftRight className="w-8 h-8" />,
        difficulty: "Scaling",
        estimatedTime: "1–5 min",
        color: "from-orange-500 to-orange-600",
        status: "Test your word knowledge",
        rewards: "Earn coins and ELO",
        link: "/antonymsGame",
      },
    ],
  },

  comprehension: {
    title: "Comprehension",
    subtitle: "From quick word hunts to ranked battles — pick your challenge",
    games: [
      {
        id: "spot-word",
        title: "Spot the Word",
        description: "Scan the passage, hunt the word — how fast can you spot it?",
        icon: <Search className="w-8 h-8" />,
        difficulty: "Easy–Medium",
        estimatedTime: "3–7 min",
        color: "from-green-500 to-green-700",
        status: "Quick Play",
        rewards: "+Quick XP, Accuracy Bonus",
        link: "/levelSelector",
        levelSelectorTitle: "Spot the Word",
        gameLink: "/spotWordGame",
      },
      {
        id: "comprehension",
        title: "Comprehension",
        description: "Read, think, and pick the right answer — your comprehension skills will grow every round",
        icon: <BookOpen className="w-8 h-8" />,
        difficulty: "Adaptive",
        estimatedTime: "8–12 min",
        color: "from-indigo-500 to-indigo-700",
        status: "Level Adaptive",
        rewards: "+Base XP, Comprehension Points",
        link: "/levelSelector",
        levelSelectorTitle: "Comprehension Practice",
        gameLink: "/comprehensionGame",
      },
      {
        id: "MCQ-master",
        title: "MCQ Master",
        description: "The clock is ticking — read fast, think faster, and rack up your score",
        icon: <Zap className="w-8 h-8" />,
        difficulty: "Multiple choice",
        estimatedTime: "2–5 min",
        color: "from-red-500 to-red-700",
        status: "Rush Mode",
        rewards: "+Speed Bonus, Time Master Owls",
        link: "/levelSelector",
        levelSelectorTitle: "MCQ Master",
        gameLink: "/mcq10Game",
      },
      {
        id: "ranked",
        title: "Ranked Games",
        description: "Put your skills on the line — battle other players and fight your way up the leaderboard",
        icon: <Trophy className="w-8 h-8" />,
        difficulty: "Competitive",
        estimatedTime: "10–15 min",
        color: "from-yellow-500 to-orange-600",
        status: "Season 2 Active",
        rewards: "+ELO Rating, Seasonal Rewards",
        link: "/rankedGame",
      },
    ],
  },

  books: {
    title: "Books",
    subtitle: "Great stories are waiting — read chapters, discover words, and show what you know",
    games: [
      {
        id: "book-reading",
        title: "Book Reading",
        description: "Explore real books at your own pace — tap any word for its meaning and get AI-powered explanations",
        icon: <BookMarked className="w-8 h-8" />,
        difficulty: "Self-Paced",
        estimatedTime: "10–30 min",
        color: "from-indigo-500 to-indigo-700",
        status: "Books Available",
        rewards: "+Reading XP, Story Owls",
        link: "/bookReading",
      },
      {
        id: "book-comprehension",
        title: "Book Comprehension",
        description: "Go deeper — choose a real book chapter and prove you understood every detail",
        icon: <BookOpen className="w-8 h-8" />,
        difficulty: "Adaptive",
        estimatedTime: "30–45 min",
        color: "from-indigo-500 to-indigo-700",
        status: "Real Books",
        rewards: "+Deep XP, Literature Owls",
        link: "/bookSelector",
      },
    ],
  },
};

export default function CategoryGamesPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [bookCount, setBookCount] = useState(null);

  useEffect(() => {
    if (category !== "books") return;
    apiFetch(`/api/books/catalogue`)
      .then((r) => r.json())
      .then((d) => setBookCount(Object.keys(d.bookInfo || {}).length))
      .catch(() => {});
  }, [category]);

  const data = CATEGORIES[category];

  if (!data) {
    return (
      <div style={{ padding: "3rem", textAlign: "center" }}>
        <p>Category not found.</p>
        <Link to="/home">Back</Link>
      </div>
    );
  }

  return (
    <div className="select-game-modes">
      <PageHeader pageTitle={data.title} pageSubtitle={data.subtitle} />

      <div className="game-modes-container">
        <div className="game-modes-grid">
          {data.games.map((mode) =>
            mode.comingSoon ? (
              <div key={mode.id} className="game-mode-card coming-soon-card">
                <div className="coming-soon-banner">Coming Soon</div>
                <div className="mode-icon coming-soon-icon">{mode.icon}</div>
                <div className="mode-content">
                  <h3 className="mode-title">{mode.title}</h3>
                  <p className="mode-description">{mode.description}</p>
                  <div className="mode-details">
                    <div className="detail-item">
                      <Target className="w-4 h-4" />
                      <span>{mode.difficulty}</span>
                    </div>
                    <div className="detail-item">
                      <Clock className="w-4 h-4" />
                      <span>{mode.estimatedTime}</span>
                    </div>
                  </div>
                </div>
                <div className="play-button">
                  <button className="play-btn coming-soon-btn" disabled>
                    Coming Soon
                  </button>
                </div>
              </div>
            ) : (
              <div key={mode.id} className="game-mode-card">
                <div
                  className={`mode-icon ${
                    mode.color.replace("from-", "bg-").split(" ")[0]
                  }`}
                >
                  {mode.icon}
                </div>
                <div className="mode-content">
                  <h3 className="mode-title">{mode.title}</h3>
                  <p className="mode-description">{mode.description}</p>
                  <div className="mode-details">
                    <div className="detail-item">
                      <Target className="w-4 h-4" />
                      <span>{mode.difficulty}</span>
                    </div>
                    <div className="detail-item">
                      <Clock className="w-4 h-4" />
                      <span>{mode.estimatedTime}</span>
                    </div>
                  </div>
                  <div className="mode-status">
                    <span className="status-text">
                      {mode.id === "book-reading" && bookCount !== null
                        ? `${bookCount} Books Available`
                        : mode.status}
                    </span>
                  </div>
                  <div className="mode-rewards">
                    <span className="rewards-text">{mode.rewards}</span>
                  </div>
                </div>
                <div className="play-button">
                  <button
                    className="play-btn"
                    onClick={() =>
                      navigate(mode.link, {
                        state: mode.levelSelectorTitle
                          ? { link: mode.gameLink, title: mode.levelSelectorTitle }
                          : { fromGameSelect: true },
                      })
                    }
                  >
                    Play →
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
